// زرّ «📢 إعلان» — مُنشئ الإعلان للمالك/الأدمن، مستقلٌّ عن جرس الإشعارات.
//
// يفتح درجًا حقيقيًا (نفس الدرج المشترك: `fixed` عبر Portal إلى body)، فلا يوسّع الصفحة ولا
// يزيح تخطيطها ولا يُنشئ تمريرًا أفقيًا. والإرسال يمرّ على `/admin/announcements` القائم —
// والخادم يفرض أن الأدمن وحده يرسل، فظهور الزرّ ترتيبٌ للواجهة لا حماية.
import { useState } from 'react';
import { ApiError, post } from '../lib/api.ts';
import { Alert, Drawer, Field, Select, TextArea, TextInput } from '../components/ui.tsx';
import { useSession } from '../auth/session.tsx';

const SEVERITIES = [
  { code: 'INFO', ar: 'إعلان عادي' },
  { code: 'WARNING', ar: 'تنبيه' },
  { code: 'CRITICAL', ar: 'عاجل' },
];

export function AnnounceButton() {
  const { me } = useSession();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState('INFO');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setBusy(true); setError(null); setDone(null);
    try {
      const r = await post<{ sent: number }>('/admin/announcements',
        { title: title.trim(), body: body.trim(), severity });
      setDone(`أُرسل الإعلان إلى ${r.sent} موظفًا. يظهر لهم نصّه كاملًا فور فتح الشاشة.`);
      setTitle(''); setBody('');
    } catch (e) { setError(e instanceof ApiError ? e.message : 'تعذّر إرسال الإعلان'); }
    finally { setBusy(false); }
  }

  const ready = title.trim().length >= 2 && body.trim().length >= 2;

  return (
    <>
      <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => setOpen(true)}
        aria-haspopup="dialog" aria-label="إرسال إعلان للموظفين">
        <span aria-hidden="true">📢</span> إعلان
      </button>

      {open && (
        <Drawer title="📢 إرسال إعلان" side="right" tone="brand" onClose={() => setOpen(false)}>
          <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-card">
            <p className="text-sm text-ink-700">
              المرسِل: <strong>{me?.fullNameAr ?? ''}</strong>{me?.isOwner ? ' (المالك)' : ''}
            </p>
            <p className="mt-1 text-xs text-ink-500">
              يصل الإعلان لكل الموظفين النشطين، ويظهر لهم <strong>بنصّه كاملًا</strong> فور فتح
              الشاشة — لا يحتاج أحدهم أن يفتح رسالةً ليعرف ما فيها.
            </p>
          </section>

          <div className="mt-3 space-y-3">
            <Field label="العنوان" required>
              <TextInput value={title} maxLength={120} onChange={e => setTitle(e.target.value)} />
            </Field>
            <Field label="النوع">
              <Select value={severity} options={SEVERITIES} onChange={e => setSeverity(e.target.value)} />
            </Field>
            <Field label="نصّ الإعلان" required>
              <TextArea rows={6} value={body} maxLength={4000} onChange={e => setBody(e.target.value)} />
            </Field>
          </div>

          {error && <div className="mt-3"><Alert>{error}</Alert></div>}
          {done && <div className="mt-3"><Alert kind="success">{done}</Alert></div>}

          <button type="button" className="btn-primary mt-4 w-full px-4 py-2"
            disabled={!ready || busy} onClick={() => void send()}>
            {busy ? 'جارٍ الإرسال…' : 'إرسال الإعلان'}
          </button>
        </Drawer>
      )}
    </>
  );
}
